import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScheduleEvent, DateRange, EmbeddedToHostMessage } from '../types';
import type {
  GroupTherapyConfig,
  GroupTherapyHandle,
  GroupTherapyMachineState,
  GroupSession,
  GroupSessionsStatePayload,
  GroupEnrollOutcomePayload,
  GroupAttendancePayload,
  GroupConflictResultPayload,
  GroupCommandPayload,
  GroupAttendanceRequestPayload,
  PendingEnrollment,
} from './groupTypes';

const SESSION_TIMEOUT_MS = 5_000;
const CONFLICT_TIMEOUT_MS = 4_000;
const ENROLL_TIMEOUT_MS = 8_000;

export function useGroupTherapy(
  config: GroupTherapyConfig | undefined,
  emit: (msg: EmbeddedToHostMessage) => void,
  viewRange: DateRange,
  events: ScheduleEvent[],
  visibleResourceIds: string[] | undefined,
): GroupTherapyHandle {
  const enabled = config?.enabled ?? false;

  const [machineState, setMachineState] = useState<GroupTherapyMachineState>('IDLE');
  const [sessionsMap, setSessionsMap] = useState<Map<string, GroupSession>>(new Map());
  const [pendingEnrollment, setPendingEnrollment] = useState<PendingEnrollment | null>(null);
  const [attendanceData, setAttendanceData] = useState<GroupAttendancePayload | null>(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  const machineStateRef = useRef<GroupTherapyMachineState>('IDLE');
  const configRef = useRef(config);
  const eventsRef = useRef(events);
  const viewRangeRef = useRef(viewRange);
  const visibleResourceIdsRef = useRef(visibleResourceIds);
  const pendingEnrollmentRef = useRef<PendingEnrollment | null>(null);
  const onGroupSessionsReceivedRef = useRef<(p: GroupSessionsStatePayload) => void>(() => undefined);
  const onGroupConflictResultReceivedRef = useRef<(p: GroupConflictResultPayload) => void>(() => undefined);
  const onGroupEnrollOutcomeReceivedRef = useRef<(p: GroupEnrollOutcomePayload) => void>(() => undefined);

  useEffect(() => { configRef.current = config; }, [config]);
  useEffect(() => { eventsRef.current = events; }, [events]);
  useEffect(() => { viewRangeRef.current = viewRange; }, [viewRange]);
  useEffect(() => { visibleResourceIdsRef.current = visibleResourceIds; }, [visibleResourceIds]);
  useEffect(() => { pendingEnrollmentRef.current = pendingEnrollment; }, [pendingEnrollment]);

  const transition = useCallback((next: GroupTherapyMachineState) => {
    machineStateRef.current = next;
    setMachineState(next);
  }, []);

  const requestGroupSessions = useCallback(() => {
    if (!configRef.current?.enabled) return;
    const vr = viewRangeRef.current;
    const resourceIds = visibleResourceIdsRef.current
      ?? eventsRef.current.map((e) => e.resourceId).filter((id): id is string => !!id);

    const payload = {
      visibleRangeStart: vr.startUtc,
      visibleRangeEnd: vr.endUtc,
      resourceIds: [...new Set(resourceIds)],
    };

    const result = configRef.current.onRequestGroupSessions(payload);
    emit({ type: 'GROUP_SESSIONS_REQUEST', payload });
    transition('AWAITING_STATE');

    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<GroupSessionsStatePayload>).then((sessions) => {
        if (machineStateRef.current === 'AWAITING_STATE') {
          onGroupSessionsReceivedRef.current(sessions);
        }
      }).catch(() => {
        if (machineStateRef.current === 'AWAITING_STATE') transition('IDLE');
      });
    }
  }, [emit, transition]);

  // Fire on mount and range changes.
  const prevRangeKey = useRef('');
  useEffect(() => {
    if (!enabled) return;
    const rangeKey = `${viewRange.startUtc}|${viewRange.endUtc}`;
    if (prevRangeKey.current === rangeKey) return;
    prevRangeKey.current = rangeKey;
    requestGroupSessions();
  }, [enabled, viewRange, requestGroupSessions]);

  // Re-request when the set of isGroupTherapy events (or their capacity/clients) changes.
  const prevGroupEventsKey = useRef('');
  useEffect(() => {
    if (!enabled) return;
    const key = events
      .filter((e) => e.isGroupTherapy)
      .map((e) => `${e.id}:${e.groupCapacity ?? ''}:${(e.groupClientIds ?? []).slice().sort().join('+')}`)
      .sort()
      .join('|');
    if (prevGroupEventsKey.current === key) return;
    prevGroupEventsKey.current = key;
    if (key) requestGroupSessions();
  }, [enabled, events, requestGroupSessions]);

  // Timeout: revert to IDLE if no GROUP_SESSIONS_STATE arrives.
  useEffect(() => {
    if (machineState !== 'AWAITING_STATE') return;
    const id = setTimeout(() => {
      if (machineStateRef.current === 'AWAITING_STATE') transition('IDLE');
    }, SESSION_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [machineState, transition]);

  const onGroupSessionsReceived = useCallback((payload: GroupSessionsStatePayload) => {
    const newMap = new Map<string, GroupSession>();
    for (const [eventId, session] of Object.entries(payload.sessions)) {
      newMap.set(eventId, session);
    }
    setSessionsMap(newMap);
    transition('GROUP_ACTIVE');
  }, [transition]);

  useEffect(() => { onGroupSessionsReceivedRef.current = onGroupSessionsReceived; }, [onGroupSessionsReceived]);

  // ── Enrollment flow ──────────────────────────────────────────────────────

  const startEnrollment = useCallback((payload: {
    eventId: string;
    groupId: string;
    groupName: string;
    patientId: string;
    displayLabel: string;
    start: string;
    end: string;
    resourceIds: string[];
  }) => {
    if (!configRef.current?.enabled) return;

    const enrollment: PendingEnrollment = {
      eventId: payload.eventId,
      groupId: payload.groupId,
      groupName: payload.groupName,
      patientId: payload.patientId,
      displayLabel: payload.displayLabel,
      start: payload.start,
      end: payload.end,
      dialogState: 'checking_conflict',
    };
    setPendingEnrollment(enrollment);

    const conflictPayload = {
      patientId: payload.patientId,
      groupId: payload.groupId,
      eventId: payload.eventId,
      start: payload.start,
      end: payload.end,
      resourceIds: payload.resourceIds,
    };

    const result = configRef.current.onCheckConflict(conflictPayload);
    emit({ type: 'GROUP_CONFLICT_CHECK', payload: conflictPayload });

    if (result && typeof (result as Promise<unknown>).then === 'function') {
      const timer = setTimeout(() => {
        // Conflict check timed out — proceed anyway with no conflict data.
        setPendingEnrollment((prev) =>
          prev?.dialogState === 'checking_conflict'
            ? { ...prev, dialogState: 'awaiting_confirm' }
            : prev,
        );
      }, CONFLICT_TIMEOUT_MS);

      (result as Promise<GroupConflictResultPayload>).then((conflictResult) => {
        clearTimeout(timer);
        onGroupConflictResultReceivedRef.current(conflictResult);
      }).catch(() => {
        clearTimeout(timer);
        setPendingEnrollment((prev) =>
          prev?.dialogState === 'checking_conflict'
            ? { ...prev, dialogState: 'awaiting_confirm' }
            : prev,
        );
      });
    }
  }, [emit]);

  const onGroupConflictResultReceived = useCallback((payload: GroupConflictResultPayload) => {
    setPendingEnrollment((prev) => {
      if (!prev || prev.eventId !== payload.eventId) return prev;
      return {
        ...prev,
        conflictResult: payload,
        dialogState: 'awaiting_confirm',
      };
    });
    emit({ type: 'GROUP_CONFLICT_RESULT', payload });
  }, [emit]);

  useEffect(() => {
    onGroupConflictResultReceivedRef.current = onGroupConflictResultReceived;
  }, [onGroupConflictResultReceived]);

  const confirmEnrollment = useCallback(() => {
    const current = pendingEnrollmentRef.current;
    if (!current || current.dialogState !== 'awaiting_confirm') return;
    if (!configRef.current?.enabled) return;

    setPendingEnrollment((prev) => prev ? { ...prev, dialogState: 'enrolling' } : prev);

    const enrollPayload = {
      eventId: current.eventId,
      groupId: current.groupId,
      patientId: current.patientId,
      displayLabel: current.displayLabel,
      requestedAt: new Date().toISOString(),
      conflictCheckResult: current.conflictResult,
    };

    const result = configRef.current.onEnrollRequest(enrollPayload);
    emit({ type: 'GROUP_ENROLL_REQUEST', payload: enrollPayload });

    if (result && typeof (result as Promise<unknown>).then === 'function') {
      const timer = setTimeout(() => {
        setPendingEnrollment((prev) =>
          prev?.dialogState === 'enrolling'
            ? { ...prev, dialogState: 'awaiting_confirm' }
            : prev,
        );
      }, ENROLL_TIMEOUT_MS);

      (result as Promise<GroupEnrollOutcomePayload>).then((outcome) => {
        clearTimeout(timer);
        onGroupEnrollOutcomeReceivedRef.current(outcome);
      }).catch(() => {
        clearTimeout(timer);
        setPendingEnrollment((prev) =>
          prev?.dialogState === 'enrolling'
            ? { ...prev, dialogState: 'awaiting_confirm' }
            : prev,
        );
      });
    }
  }, [emit]);

  const cancelEnrollment = useCallback(() => {
    setPendingEnrollment(null);
  }, []);

  const onGroupEnrollOutcomeReceived = useCallback((payload: GroupEnrollOutcomePayload) => {
    setPendingEnrollment((prev) => {
      if (!prev || prev.eventId !== payload.eventId) return prev;
      return { ...prev, dialogState: 'done', outcome: payload };
    });

    // Update sessions map with fresh session data from host.
    if (payload.updatedSession) {
      const session = payload.updatedSession;
      setSessionsMap((prev) => {
        const next = new Map(prev);
        next.set(payload.eventId, session);
        return next;
      });
    }
    emit({ type: 'GROUP_ENROLL_OUTCOME', payload });
  }, [emit]);

  useEffect(() => {
    onGroupEnrollOutcomeReceivedRef.current = onGroupEnrollOutcomeReceived;
  }, [onGroupEnrollOutcomeReceived]);

  // ── Attendance / roster ──────────────────────────────────────────────────

  const requestAttendance = useCallback((payload: GroupAttendanceRequestPayload) => {
    if (!configRef.current?.enabled) return;
    setAttendanceLoading(true);

    const result = configRef.current.onRequestAttendance(payload);
    emit({ type: 'GROUP_ATTENDANCE_REQUEST', payload });

    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<GroupAttendancePayload>).then((attendance) => {
        setAttendanceData(attendance);
        setAttendanceLoading(false);
      }).catch(() => {
        setAttendanceLoading(false);
      });
    }
  }, [emit]);

  const onGroupAttendanceReceived = useCallback((payload: GroupAttendancePayload) => {
    setAttendanceData(payload);
    setAttendanceLoading(false);
  }, []);

  const clearAttendance = useCallback(() => {
    setAttendanceData(null);
  }, []);

  // ── AI command handler ───────────────────────────────────────────────────

  const handleGroupCommand = useCallback((payload: GroupCommandPayload) => {
    configRef.current?.onGroupCommand?.(payload);
    emit({ type: 'GROUP_COMMAND', payload });
    // Refresh session state after any command.
    requestGroupSessions();
  }, [emit, requestGroupSessions]);

  if (!enabled) {
    return {
      enabled: false,
      machineState: 'IDLE',
      sessionsMap: new Map(),
      pendingEnrollment: null,
      attendanceData: null,
      attendanceLoading: false,
      startEnrollment: () => undefined,
      confirmEnrollment: () => undefined,
      cancelEnrollment: () => undefined,
      requestAttendance: () => undefined,
      clearAttendance: () => undefined,
      onGroupSessionsReceived: () => undefined,
      onGroupEnrollOutcomeReceived: () => undefined,
      onGroupAttendanceReceived: () => undefined,
      onGroupConflictResultReceived: () => undefined,
      handleGroupCommand: () => undefined,
      requestGroupSessions: () => undefined,
    };
  }

  return {
    enabled,
    machineState,
    sessionsMap,
    pendingEnrollment,
    attendanceData,
    attendanceLoading,
    startEnrollment,
    confirmEnrollment,
    cancelEnrollment,
    requestAttendance,
    clearAttendance,
    onGroupSessionsReceived,
    onGroupEnrollOutcomeReceived,
    onGroupAttendanceReceived,
    onGroupConflictResultReceived,
    handleGroupCommand,
    requestGroupSessions,
  };
}
